import React, { useEffect, useRef, useCallback, useState } from "react";
import styles from "./ConnectionsSection.module.css";
import SelectableConnectionRow from "./SelectableConnectionRow";
import Spinner from "../button/Spinner";
import { ConnectionEdge, NetworkType, PageInfo, PriorityType, UrgencyType } from "../../gql/types";
import blackGroupIcon from "../../assets/icons/blackGroupIcon.svg";
import whiteGroupIcon from "../../assets/icons/whiteGroupIcon.svg";
import whiteSchoolIcon from "../../assets/icons/whiteSchoolIcon.svg";
import blackSchoolIcon from "../../assets/icons/blackSchoolIcon.svg";
import hornIcon from "../../assets/icons/horn.svg";
import blackHornIcon from "../../assets/icons/blackHornIcon.svg";
import downArrowIcon from '../../assets/icons/downArrow.svg';
import upArrowIcon from '../../assets/icons/upArrow.svg';
import SelectionIndicator from "./SelectionIndicator";
import { ConnectionPlacementType } from "./constants";
import InOverlaySelectableConnectionRow from "./InOverlaySelectableConnectionRow";

export interface ConnectionsSectionProps {
    /** Display Info */
    title: string;
    networkType: NetworkType;
    priorityType: PriorityType;
    urgencyType: UrgencyType;
    message?: string | null;

    /** Data */
    edges: ConnectionEdge[];
    count: number;

    /** Loading & Pagination */
    loading: boolean;
    isPaginated: boolean;
    pageInfo?: PageInfo;
    loadMore?: (cursor?: string | null) => Promise<void>;

    /** Selection State */
    isSelectMode: boolean;
    selectedIds: Set<string>;
    onToggle: (connectionIds: string[]) => void;

    openSection?: boolean;

    // Controls the look and feel
    placementType?: ConnectionPlacementType;
}

const getContainerClassName = (
    placementType: ConnectionPlacementType,
    priorityType: PriorityType
): string => {
    const base = styles.sectionContainer;
    const priority = priorityType === PriorityType.High ? styles.highPriorityContainer : "";

    if (placementType === ConnectionPlacementType.OVERLAY_LIST) {
        return `${styles.overlaySectionContainer} ${priority}`;
    }
    return `${base} ${priority}`;
};

const getHeaderClassName = (
    placementType: ConnectionPlacementType,
): string => {
    switch (placementType) {
        case ConnectionPlacementType.CONNECTION_HUB:
            return styles.header;
        case ConnectionPlacementType.OVERLAY_LIST:
            return styles.overlayHeader;
    }
}

const getSectionIcon = (networkType: NetworkType, urgencyType: UrgencyType, priorityType: PriorityType) => {
    let priorityStyle = '';

    if (urgencyType === UrgencyType.High) {
        priorityStyle = styles.urgentHighPriorityIconContainer;
    } else if (priorityType === PriorityType.High) {
        priorityStyle = styles.highPriorityIconContainer;
    }

    return (
        <div className={`${styles.iconContainer} ${priorityStyle}`}>
            {getNetworkIcon(networkType, priorityType)}
        </div>
    );
};

const getNetworkIcon = (networkType: NetworkType, priorityType: PriorityType) => {
    switch (networkType) {
        case NetworkType.School:
            if (priorityType === PriorityType.High) {
                return <img src={blackSchoolIcon} alt="School Icon" className={`${styles.icon}`} />;
            }
            return <img src={whiteSchoolIcon} alt="School Icon" className={styles.icon} />;
        case NetworkType.Direct:
            if (priorityType === PriorityType.High) {
                return <img src={blackGroupIcon} alt="Direct Icon" className={`${styles.icon}`} />;
            }
            return <img src={whiteGroupIcon} alt="Direct Icon" className={styles.icon} />;
        default:
            if (priorityType === PriorityType.High) {
                return <img src={blackHornIcon} alt="Horn Icon" className={`${styles.icon}`} />;
            }
            return <img src={hornIcon} alt="Horn Icon" className={styles.icon} />;
    }
};

const ConnectionsSection: React.FC<ConnectionsSectionProps> = ({
    title,
    networkType,
    priorityType,
    urgencyType,
    message,
    edges,
    count,
    loading,
    isPaginated,
    pageInfo,
    loadMore,
    isSelectMode,
    selectedIds,
    onToggle,
    openSection,
    placementType = ConnectionPlacementType.CONNECTION_HUB,
}) => {
    const [isOpen, setIsOpen] = useState(openSection ?? (message && message !== null));
    const [isHeaderSelected, setIsHeaderSelected] = useState(false);
    const sentinelRef = useRef<HTMLDivElement>(null);

    const handleLoadMore = useCallback(() => {
        if (isPaginated && loadMore && pageInfo?.hasNextPage && !loading) {
            loadMore(pageInfo.endCursor);
        }
    }, [isPaginated, loadMore, pageInfo, loading]);

    useEffect(() => {
        if (!isPaginated || !sentinelRef.current || !pageInfo?.hasNextPage) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) handleLoadMore();
            },
            { threshold: 1.0 }
        );
        observer.observe(sentinelRef.current);
        return () => observer.disconnect();
    }, [handleLoadMore, isPaginated, pageInfo]);

    let priorityStyle = '';

    if (priorityType === PriorityType.High) {
        priorityStyle = styles.highPriorityContainer;
    }

    const handleHeaderToggle = () => {
        const nextValue = !isHeaderSelected;
        setIsHeaderSelected(nextValue);

        const connectionIds = edges.map((edge) => edge.node.connectionID);

        if (nextValue) {
            // Select all: send all unselected IDs
            const unselected = connectionIds.filter(id => !selectedIds.has(id));
            onToggle(unselected);
        } else {
            // Deselect all: send all currently selected IDs in this section
            const toUnselect = connectionIds.filter(id => selectedIds.has(id));
            onToggle(toUnselect);
        }
    };

    const renderHeader = () => {
        return (
            <div className={getHeaderClassName(placementType)} onClick={() => setIsOpen(prev => !prev)}>
                {placementType === ConnectionPlacementType.CONNECTION_HUB && isSelectMode && !isPaginated && (
                    <SelectionIndicator
                        isSelected={isHeaderSelected}
                        onToggle={handleHeaderToggle}
                    />
                )}
                <div className={`${styles.left} ${isSelectMode ? styles.leftShifted : ""}`}>
                    {placementType === ConnectionPlacementType.CONNECTION_HUB &&
                        getSectionIcon(networkType, urgencyType, priorityType)}
                    <div className={styles.headerTextContainer}>
                        <span className={styles.title}>{title}</span>
                        {(!loading || count > 0) && <span className={styles.count}> ({count})</span>}
                    </div>
                </div>
                <div className={styles.chevronContainer}>
                    <img
                        src={isOpen ? upArrowIcon : downArrowIcon}
                        alt={isOpen ? "Collapse section" : "Expand section"}
                        className={styles.chevronIcon}
                    />
                </div>
            </div>
        );
    };

    useEffect(() => {
        if (edges.length === 0) return;
        const allSelected = edges.every(edge => selectedIds.has(edge.node.connectionID));
        setIsHeaderSelected(allSelected);
    }, [selectedIds, edges]);

    return (
        <div className={getContainerClassName(placementType, priorityType)}>
            {renderHeader()}

            {isOpen && placementType === ConnectionPlacementType.CONNECTION_HUB && (
                <div className={styles.contentDividerContainer}>
                    <div className={styles.contentDivider}>
                    </div>
                </div>
            )}

            {isOpen && (
                <div className={`
                    ${styles.scrollArea}
                    ${placementType === ConnectionPlacementType.OVERLAY_LIST
                        ? styles.overlayScrollArea
                        : ''
                    }`}>
                    {loading && edges.length === 0 ? (
                        <div className={styles.spinnerContainer}>
                            <Spinner />
                        </div>
                    ) : (
                        <>
                            {edges.length === 0 && message !== null && <div className={styles.messageContainer}>
                                <div className={styles.message}>
                                    {message}
                                </div>
                            </div>}
                            {edges.map((edge, index) => (
                                <React.Fragment key={edge.cursor}>
                                    {placementType === ConnectionPlacementType.CONNECTION_HUB ? (
                                        <>
                                            <SelectableConnectionRow
                                                connection={edge.node}
                                                isSelected={selectedIds.has(edge.node.connectionID)}
                                                isSelectMode={isSelectMode}
                                                onToggle={() => onToggle([edge.node.connectionID])}
                                            />
                                            {index !== edges.length - 1 && <div className={styles.rowDivider} />}
                                        </>
                                    ) : (
                                        <>
                                            <InOverlaySelectableConnectionRow
                                                connection={edge.node}
                                                isSelected={selectedIds.has(edge.node.connectionID)}
                                                onToggle={() => onToggle([edge.node.connectionID])}
                                            />
                                        </>
                                    )}
                                </React.Fragment>
                            ))}
                            {isPaginated && pageInfo?.hasNextPage && (
                                <div ref={sentinelRef} className={styles.sentinel}>
                                    {loading && <Spinner />}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div >
    );
};

export default ConnectionsSection;
